import React, { useState, useRef, useEffect } from 'react';
import ViewToggle from './ViewToggle';
import { useNavigate } from 'react-router-dom';
import OnlineToggle from './OnlineToggle';
import DashboardGuideComponent from './DashboardGuide';
import SunshineManager from './SunshineManager';
import { getCsrfToken } from '../utils/auth';
import tocaroLogo from '../assets/tocaroLogoWhite.webp';

const DEVICE_TYPE_OPTIONS = [
  { value: 'apollocore', label: 'ApolloCore' },
  { value: 'proteuscore', label: 'ProteusCore' },
  { value: 'macmini', label: 'Mac Mini' },
];

export default function DashboardHeader({
  admin,
  companyList,
  selectedCompany,
  setSelectedCompany,
  onlyShowOnline,
  setOnlyShowOnline,
  selectedDeviceTypes,
  setSelectedDeviceTypes,
  currentView,
  setCurrentView,
  onlineDevkits,
  totalDevkits,
  onNavigate,
  setUser,
  setAdmin,
  setSidebarOpen,
  selectedDevkitCount = 0
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [showGuide, setShowGuide] = useState(false);
  const [showSunshineManager, setShowSunshineManager] = useState(false);
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  const typeFilterRef = useRef(null);

  // Dark mode: toggles the `dark` class on <html> (see the .dark overrides in
  // index.css) and persists across sessions.
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', darkMode ? 'true' : 'false');
  }, [darkMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeFilterRef.current && !typeFilterRef.current.contains(event.target)) {
        setIsTypeFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedTypesArr = Array.isArray(selectedDeviceTypes) ? selectedDeviceTypes : [];
  const toggleType = (value) => {
    if (!setSelectedDeviceTypes) return;
    const next = selectedTypesArr.includes(value)
      ? selectedTypesArr.filter((v) => v !== value)
      : [...selectedTypesArr, value];
    setSelectedDeviceTypes(next);
  };
  const typeFilterLabel = (() => {
    if (selectedTypesArr.length === 0) return 'All Device Types';
    if (selectedTypesArr.length === DEVICE_TYPE_OPTIONS.length) return 'All Device Types';
    if (selectedTypesArr.length <= 2) {
      return selectedTypesArr
        .map((v) => DEVICE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v)
        .join(', ');
    }
    return `${selectedTypesArr.length} types`;
  })();

  let navigate;
  try {
    navigate = useNavigate();
  } catch (error) {
    navigate = onNavigate;
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      const res = await fetch(`/api/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCsrfToken()
        }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.msg || 'Logout failed');
      }
      localStorage.removeItem('currentUser');
      localStorage.removeItem('sessionToken');
      setUser(null);
      setAdmin(false);
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const BatchUpdateButton = () => {
    return (
      <button
        onClick={onOpenBatchUpdate}
        className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-sm rounded-xl px-4 py-3 border border-slate-700/40 shadow-sm transition-all duration-200 hover:scale-105 active:scale-95 hover:border-slate-600/60 group flex items-center gap-2 min-w-[140px]"
        title="Open batch update panel"
      >
        <svg className="w-4 h-4 text-gray-200 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
        <div className="text-center">
          <div className="text-sm font-semibold text-white">
            Batch Update
          </div>
          <div className="text-xs text-slate-400 group-hover:text-slate-300 -mt-0.5 transition-colors">
            {selectedDevkitCount > 0 ? `${selectedDevkitCount} selected` : `${onlineDevkits} available`}
          </div>
        </div>
      </button>
    );
  };

  const HamburgerMenu = () => {
    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="group p-3 hover:bg-white/10 rounded-xl transition-all duration-300 ease-out hover:scale-105 backdrop-blur-sm border border-white/10"
          aria-label="Menu"
        >
          <svg
            className="h-5 w-5 text-gray-200 group-hover:text-white transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {isMenuOpen && (
          <div className="absolute left-0 mt-3 w-64 bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 py-2 z-50 animate-in slide-in-from-top-2 duration-200">
            <div className="px-4 py-3 border-b border-slate-700/50">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Dashboard Menu</p>
            </div>
            
            <button
              onClick={() => {
                setIsMenuOpen(false);
                setSidebarOpen(true);
              }}
              className="w-full text-left px-4 py-3 text-gray-200 hover:bg-slate-800/80 hover:text-white transition-all duration-200 flex items-center gap-3 group"
            >
              <div className="p-2 rounded-lg bg-blue-500/20 group-hover:bg-blue-500/30 transition-colors">
                <svg
                  className="h-4 w-4 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium">Batch Update</div>
                <div className="text-xs text-gray-400">
                  Update multiple devkits
                </div>
              </div>
            </button>
            

            {admin && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  setShowSunshineManager(true);
                }}
                className="w-full text-left px-4 py-3 text-gray-200 hover:bg-slate-800/80 hover:text-white transition-all duration-200 flex items-center gap-3 group"
              >
                <div className="p-2 rounded-lg bg-purple-500/20 group-hover:bg-purple-500/30 transition-colors">
                  <svg
                    className="h-4 w-4 text-purple-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2zM10 7.5l4.5 2.5-4.5 2.5v-5zM9 21h6"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Sunshine Streaming</div>
                  <div className="text-xs text-gray-400">
                    Manage devkit stream pairings
                  </div>
                </div>
              </button>
            )}

            {admin && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  navigate('/insights');
                }}
                className="w-full text-left px-4 py-3 text-gray-200 hover:bg-slate-800/80 hover:text-white transition-all duration-200 flex items-center gap-3 group"
              >
                <div className="p-2 rounded-lg bg-emerald-500/20 group-hover:bg-emerald-500/30 transition-colors">
                  <svg
                    className="h-4 w-4 text-emerald-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Usage Insights</div>
                  <div className="text-xs text-gray-400">
                    Device &amp; customer activity
                  </div>
                </div>
              </button>
            )}

            <button
              onClick={() => {
                setIsMenuOpen(false);
                setShowGuide(true);
              }}
              className="w-full text-left px-4 py-3 text-gray-200 hover:bg-slate-800/80 hover:text-white transition-all duration-200 flex items-center gap-3 group"
            >
              <div className="p-2 rounded-lg bg-gray-500/20 group-hover:bg-gray-500/30 transition-colors">
                <svg
                  className="h-4 w-4 text-current"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium">Dashboard Guide</div>
                <div className="text-xs text-gray-400">Learn how to use the dashboard</div>
              </div>
            </button>

            <button
              onClick={() => setDarkMode((v) => !v)}
              className="w-full text-left px-4 py-3 text-gray-200 hover:bg-slate-800/80 hover:text-white transition-all duration-200 flex items-center gap-3 group"
            >
              <div className="p-2 rounded-lg bg-indigo-500/20 group-hover:bg-indigo-500/30 transition-colors">
                <svg
                  className="h-4 w-4 text-indigo-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div className="font-medium">Dark Mode</div>
                <div className="text-xs text-gray-400">{darkMode ? 'On' : 'Off'}</div>
              </div>
              <div
                className={`w-9 h-5 rounded-full relative transition-colors duration-200 ${darkMode ? 'bg-indigo-500' : 'bg-slate-600'}`}
                aria-hidden
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all duration-200 ${darkMode ? 'left-[18px]' : 'left-0.5'}`}
                />
              </div>
            </button>

            <div className="my-2 px-4">
              <hr className="border-slate-700/50" />
            </div>

            <button
              onClick={handleSignOut}
              className="w-full text-left px-4 py-3 text-gray-200 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200 flex items-center gap-3 group"
            >
              <div className="p-2 rounded-lg bg-red-500/20 group-hover:bg-red-500/30 transition-colors">
                <svg
                  className="h-4 w-4 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium">Sign Out</div>
                <div className="text-xs text-gray-400">End your session</div>
              </div>
            </button>
          </div>
        )}
      </div>
    );
  };

  const LogoSection = () => {
    return (
      <div className="flex items-center gap-4">
        <a href="https://www.tocaroblue.com" className="group">
          <img
            src={tocaroLogo}
            alt="Tocaro Blue Logo"
            className="h-12 w-auto object-contain"
          />
        </a>

        <div className="border-l border-slate-600/50 pl-4">
          <h1 className="text-1xl lg:text-xl font-bold bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
            DevKit Dashboard
          </h1>
          <p className="text-slate-400 text-sm mt-1 font-medium">
            Manage and monitor your ProteusCore development kits
          </p>
        </div>
      </div>
    );
  }

  const DevKitStatus = ({ onlineDevkits, totalDevkits, onlyShowOnline, setOnlyShowOnline }) => {
    const handleClick = () => {
      setOnlyShowOnline(!onlyShowOnline);
    };

    return (
      <button
        onClick={handleClick}
        className={`bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-sm rounded-xl px-4 py-3 border shadow-sm transition-all duration-200 hover:scale-105 active:scale-95 group w-32 ${onlyShowOnline
            ? 'border-emerald-500/50 ring-1 ring-emerald-500/30'
            : 'border-slate-700/40 hover:border-slate-600/60'
          }`}
        title={onlyShowOnline ? "Click to show all DevKits" : "Click to show only online DevKits"}
      >
        <div className="text-center">
          <div className="text-lg font-bold text-white tabular-nums">
            <span className={onlineDevkits > 0 ? "text-emerald-400" : "text-red-400"}>
              {onlineDevkits}
            </span>
            <span className="text-slate-500 mx-1">/</span>
            <span className="text-slate-300">{totalDevkits}</span>
          </div>
          <div className={`text-xs font-medium uppercase tracking-wider mt-0.5 transition-colors h-4 flex items-center justify-center ${onlyShowOnline
              ? 'text-emerald-300'
              : 'text-slate-400 group-hover:text-slate-300'
            }`}>
            <span className="whitespace-nowrap">
              {onlyShowOnline ? 'Online Only' : 'DevKits Online'}
            </span>
          </div>
        </div>
      </button>
    );
  };

  return (
    // Fragment, not a wrapper div: `sticky` can only travel within its
    // parent, and a wrapper div is exactly header-height — the header must
    // sit directly in the page's scrolling column to stick.
    <>
      <header className="
  sticky top-0 z-50 w-full
  bg-gradient-to-r from-gray-800 via-gray-900 to-black
  border-b border-gray-700 shadow-md
">
        <div className="mx-auto w-full max-w-[85rem] px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4 md:gap-6">
            <div className="flex items-center gap-4 md:gap-6 min-w-0 flex-1">
              <HamburgerMenu />
              <LogoSection />
              
            </div>

            <div className="
        flex flex-wrap items-center justify-start md:justify-end
        gap-3 md:gap-4 min-w-0 w-full md:w-auto
      ">
              {admin && (
                <div className="shrink-0">
                  <div className="relative">
                    <select
                      value={selectedCompany ?? ''}
                      onChange={(e) =>
                        setSelectedCompany(e.target.value === '' ? null : e.target.value)
                      }
                      className="appearance-none text-sm px-3 py-3 pr-8 rounded-lg border border-slate-600/50 bg-slate-800/80 backdrop-blur-sm text-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 hover:bg-slate-700/80 min-w-[140px]"
                    >
                      <option value="">All Companies</option>
                      {companyList.map((company) => (
                        <option key={company} value={company}>
                          {company}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              )}
              {admin && setSelectedDeviceTypes && (
                <div className="shrink-0">
                  <div className="relative" ref={typeFilterRef}>
                    <button
                      type="button"
                      onClick={() => setIsTypeFilterOpen((p) => !p)}
                      className={`appearance-none text-sm px-3 py-3 pr-8 rounded-lg border bg-slate-800/80 backdrop-blur-sm text-gray-200 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 hover:bg-slate-700/80 min-w-[140px] ${
                        selectedTypesArr.length > 0 && selectedTypesArr.length < DEVICE_TYPE_OPTIONS.length
                          ? 'border-blue-500/50 ring-1 ring-blue-500/30'
                          : 'border-slate-600/50'
                      }`}
                      title="Filter by device type"
                    >
                      {typeFilterLabel}
                    </button>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {isTypeFilterOpen && (
                      <div className="absolute left-0 mt-2 w-56 bg-slate-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/50 py-2 z-50">
                        <div className="px-3 py-2 flex items-center justify-between border-b border-slate-700/50">
                          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Device Types</span>
                          {selectedTypesArr.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedDeviceTypes([])}
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        {DEVICE_TYPE_OPTIONS.map((opt) => {
                          const checked = selectedTypesArr.includes(opt.value);
                          return (
                            <label
                              key={opt.value}
                              className="flex items-center gap-3 px-3 py-2 cursor-pointer text-gray-200 hover:bg-slate-800/80"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleType(opt.value)}
                                className="h-4 w-4 rounded border-slate-500 bg-slate-700 text-blue-500 focus:ring-blue-500/50"
                              />
                              <span className="text-sm">{opt.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="shrink-0">
                <ViewToggle currentView={currentView} onViewChange={setCurrentView} />
              </div>
              <div className="shrink-0">
                <DevKitStatus
                  onlineDevkits={onlineDevkits}
                  totalDevkits={totalDevkits}
                  onlyShowOnline={onlyShowOnline}
                  setOnlyShowOnline={setOnlyShowOnline}
                />
              </div>
            </div>
          </div>
        </div>
      </header>


      {/* Dashboard Guide Modal */}
      {showGuide && (
        <DashboardGuideComponent showGuide={showGuide} setShowGuide={setShowGuide} />
      )}

      {/* Sunshine Streaming Manager (admin) */}
      {showSunshineManager && (
        <SunshineManager onClose={() => setShowSunshineManager(false)} />
      )}
    </>
  );
}